import { Badge } from "@chakra-ui/layout";
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
} from "@chakra-ui/react";
import { BorrowingBaseSummaryTableBody } from "features/credit-facility/borrowing-base/BorrowingBaseSummaryTableBody";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { QueryBoundary } from "shared/api/QueryBoundary";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import { FormDateInput } from "shared/components/input/date/DateInput";
import { PageTabBody } from "shared/components/layout/PageTabBody";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBadges } from "shared/components/table/box/TableBoxBadges";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { Edit2Icon } from "shared/icons/Edit2Icon";
import { formatIsoDateToDisplay, nowAsIsoDate } from "shared/utils/DateUtils";

export const CreditFacilityBorrowingBaseTab: React.FC = () => {
  const { creditFacilityId } = useRequiredStringParams();
  const { t } = useTranslation(["creditFacility"]);
  const [date, setDate] = useState(nowAsIsoDate());
  const {
    isOpen: isEditDateModalOpen,
    onOpen: openEditDateModal,
    onClose: closeEditDateModal,
  } = useDisclosure();

  return (
    <PageTabBody isFullHeight={true}>
      <TableBox isFullHeight={true}>
        <TableBoxHeader>
          <TableBoxTitle>
            {t("creditFacility:borrowingBase.advanceRateGroups")}
          </TableBoxTitle>
          <TableBoxBadges>
            <Badge variant="button" onClick={openEditDateModal}>
              {formatIsoDateToDisplay(date)}
              <Edit2Icon />
            </Badge>
          </TableBoxBadges>
          <DataTableBoxActions />
        </TableBoxHeader>
        <QueryBoundary>
          <BorrowingBaseSummaryTableBody
            creditFacilityId={creditFacilityId}
            effectiveDate={date}
          />
        </QueryBoundary>
      </TableBox>
      {isEditDateModalOpen && (
        <EditDateModal
          versionDate={date}
          onSubmit={setDate}
          onCloseComplete={closeEditDateModal}
        />
      )}
    </PageTabBody>
  );
};

export interface EditDateModalProps {
  versionDate: string;
  onCloseComplete(): void;
  onSubmit: (value: string) => void;
}

interface DateEditRequest {
  versionDate: string;
}

export const EditDateModal: React.FC<EditDateModalProps> = ({
  versionDate,
  onCloseComplete,
  onSubmit,
}) => {
  const { t } = useTranslation(["creditFacility", "common"]);

  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true });

  const form = useForm<DateEditRequest>({
    defaultValues: { versionDate },
  });

  const onFormSubmit = (data: DateEditRequest) => {
    onSubmit(data.versionDate);
    onCloseComplete();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onCloseComplete={onCloseComplete}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <Form form={form} onSubmit={onFormSubmit} onSuccess={onCloseComplete}>
          <ModalHeader>
            {t("creditFacility:borrowingBase.editDateModal.title")}
            <ModalCloseButton />
          </ModalHeader>
          <ModalBody>
            <FormDateInput
              name="versionDate"
              title={t(
                "creditFacility:borrowingBase.editDateModal.dateInputTitle",
              )}
            />
          </ModalBody>
          <ModalFooter gap={2}>
            <ButtonOrder
              dismiss={<FormCancelButton onClick={onClose} autoFocus={true} />}
              confirm={
                <FormSubmitButton>{t("common:submit")}</FormSubmitButton>
              }
            />
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};
